/**
 * @cfleet/api-client - Dispatch Chat Hooks
 *
 * React Query hooks for dispatcher-side chat operations.
 * These endpoints require authentication with dispatcher role or above.
 *
 * @example
 * ```tsx
 * import {
 *   useConversationList,
 *   useConversationMessages,
 *   useSendDispatchMessage,
 *   useMarkDispatchRead,
 *   useDispatchUnreadCount,
 * } from '@cfleet/api-client';
 *
 * // List conversations
 * const { data } = useConversationList({ authConfig });
 *
 * // Get messages for a driver
 * const { data: history } = useConversationMessages('drv_123', {}, { authConfig });
 *
 * // Send message
 * const { mutate: send } = useSendDispatchMessage({ authConfig });
 * send({ driverId: 'drv_123', content: 'Hello!' });
 *
 * // Mark as read
 * const { mutate: markRead } = useMarkDispatchRead({ authConfig });
 * markRead({ driverId: 'drv_123' });
 *
 * // Unread count (for sidebar badge)
 * const { data: unread } = useDispatchUnreadCount({ authConfig });
 * ```
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DispatchChatComponents } from '../generated';
import { createAuthenticatedCoreClient, type AuthMiddlewareConfig } from '../index';

// ─────────────────────────────────────────────────────────────────────────────
// Type Aliases
// ─────────────────────────────────────────────────────────────────────────────

type ConversationSummary = DispatchChatComponents['schemas']['ConversationSummary'];
type ConversationListResponse = DispatchChatComponents['schemas']['ConversationListResponse'];
type ChatMessage = DispatchChatComponents['schemas']['ChatMessage'];
type ChatHistory = DispatchChatComponents['schemas']['ChatHistory'];
type UnreadCountResponse = DispatchChatComponents['schemas']['UnreadCountResponse'];

// Re-export types for consumers
export type {
  ConversationSummary,
  ConversationListResponse,
  ChatMessage as DispatchChatMessage,
  ChatHistory as DispatchChatHistory,
  UnreadCountResponse as DispatchUnreadCountResponse,
};

// ─────────────────────────────────────────────────────────────────────────────
// Configuration
// ─────────────────────────────────────────────────────────────────────────────

export interface UseDispatchChatHooksConfig {
  /** Authentication middleware configuration (required) */
  authConfig: AuthMiddlewareConfig;
}

/**
 * Query keys for dispatch chat queries
 */
export const dispatchChatQueryKeys = {
  all: ['dispatch-chat'] as const,
  conversations: () => [...dispatchChatQueryKeys.all, 'conversations'] as const,
  messages: (driverId: string) => [...dispatchChatQueryKeys.all, 'messages', driverId] as const,
  unread: () => [...dispatchChatQueryKeys.all, 'unread'] as const,
};

/**
 * Shared helper: create an authenticated client, execute a query, and handle errors.
 */
async function authenticatedQuery<T>(
  config: UseDispatchChatHooksConfig | undefined,
  queryFn: (
    client: ReturnType<typeof createAuthenticatedCoreClient>
  ) => Promise<{ data?: T; error?: unknown }>,
  errorMsg: string
): Promise<T> {
  if (!config?.authConfig) {
    throw new Error('Auth config required');
  }
  const client = createAuthenticatedCoreClient(config.authConfig);
  const { data, error } = await queryFn(client);
  if (error) {
    throw new Error((error as { detail?: string }).detail || errorMsg);
  }
  return data!;
}

// ─────────────────────────────────────────────────────────────────────────────
// useConversationList
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to list all driver conversations for the dispatcher's organization.
 *
 * Returns drivers sorted by most recent message with unread counts.
 */
export function useConversationList(config?: UseDispatchChatHooksConfig) {
  return useQuery({
    queryKey: dispatchChatQueryKeys.conversations(),
    queryFn: () =>
      authenticatedQuery<ConversationListResponse>(
        config,
        (client) => client.GET('/dispatch/chat/conversations'),
        'Failed to fetch conversations'
      ),
    enabled: !!config?.authConfig,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// useConversationMessages
// ─────────────────────────────────────────────────────────────────────────────

export interface ConversationMessagesParams {
  cursor?: string;
  limit?: number;
}

/**
 * Hook to get paginated message history with a specific driver.
 */
export function useConversationMessages(
  driverId: string | null | undefined,
  params?: ConversationMessagesParams,
  config?: UseDispatchChatHooksConfig
) {
  return useQuery({
    queryKey: [
      ...dispatchChatQueryKeys.messages(driverId ?? ''),
      params?.cursor ?? '',
      params?.limit ?? '',
    ],
    queryFn: () => {
      if (!driverId) {
        throw new Error('No driver ID provided');
      }
      return authenticatedQuery<ChatHistory>(
        config,
        (client) =>
          client.GET('/dispatch/chat/{driverId}/messages', {
            params: {
              path: { driverId },
              query: {
                cursor: params?.cursor,
                limit: params?.limit,
              },
            },
          }),
        'Failed to fetch messages'
      );
    },
    enabled: !!driverId && !!config?.authConfig,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// useSendDispatchMessage
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to send a message to a specific driver.
 *
 * Invalidates conversation list and messages queries on success.
 */
export function useSendDispatchMessage(config?: UseDispatchChatHooksConfig) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ driverId, content }: { driverId: string; content: string }) =>
      authenticatedQuery<ChatMessage>(
        config,
        (client) =>
          client.POST('/dispatch/chat/{driverId}/messages', {
            params: { path: { driverId } },
            body: { content },
          }),
        'Failed to send message'
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: dispatchChatQueryKeys.messages(variables.driverId),
      });
      queryClient.invalidateQueries({ queryKey: dispatchChatQueryKeys.conversations() });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// useMarkDispatchRead
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to mark all messages from a driver as read by dispatcher.
 *
 * Invalidates unread and conversation queries on success.
 */
export function useMarkDispatchRead(config?: UseDispatchChatHooksConfig) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ driverId }: { driverId: string }) =>
      authenticatedQuery<{ marked_count?: number; unread_remaining?: number }>(
        config,
        (client) =>
          client.POST('/dispatch/chat/{driverId}/read', {
            params: { path: { driverId } },
          }),
        'Failed to mark as read'
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: dispatchChatQueryKeys.unread() });
      queryClient.invalidateQueries({ queryKey: dispatchChatQueryKeys.conversations() });
      queryClient.invalidateQueries({
        queryKey: dispatchChatQueryKeys.messages(variables.driverId),
      });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// useDispatchUnreadCount
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to get total unread message count across all drivers.
 *
 * Use this for the sidebar unread badge.
 */
export function useDispatchUnreadCount(config?: UseDispatchChatHooksConfig) {
  return useQuery({
    queryKey: dispatchChatQueryKeys.unread(),
    queryFn: () =>
      authenticatedQuery<UnreadCountResponse>(
        config,
        (client) => client.GET('/dispatch/chat/unread'),
        'Failed to fetch unread count'
      ),
    enabled: !!config?.authConfig,
    refetchInterval: 5 * 60 * 1000, // 5-minute fallback; WebSocket handles real-time updates
    refetchIntervalInBackground: false, // Pause polling when tab is hidden
  });
}
